import { Link } from "@tanstack/react-router";
import { Download, Sparkles, Menu, X } from "lucide-react";
import { LanguageSwitcher } from "@/components/language-switcher";
import { useI18n } from "@/lib/i18n";
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { adminEntryPath } from "@/lib/admin-login-api";

export function SiteHeader() {
  const { t } = useI18n();
  const [mobileNav, setMobileNav] = useState(false);
  // Skip login if an admin session already exists
  const adminTo = adminEntryPath();

  const navItems = [
    { href: "#about", label: t("nav_about") },
    { href: "#features", label: t("nav_features") },
    { href: "#how", label: t("nav_how") },
    { href: "#faq", label: t("nav_faq") },
  ] as const;

  useEffect(() => {
    if (!mobileNav) {
      return;
    }
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [mobileNav]);

  const mobileMenu =
    mobileNav &&
    createPortal(
      <div className="fixed inset-0 z-[100] md:hidden" role="dialog" aria-modal="true">
        <button
          type="button"
          className="absolute inset-0 bg-transparent"
          aria-label="Close menu"
          onClick={() => setMobileNav(false)}
        />
        <nav
          className="absolute right-0 top-0 z-10 flex h-full w-[min(20rem,88vw)] flex-col gap-2 overflow-y-auto border-l border-border bg-card px-5 pb-8 pt-4 shadow-[var(--shadow-soft)]"
          style={{ backgroundColor: "hsl(var(--card))" }}
        >
          <div className="mb-2 flex items-center justify-between border-b border-border/60 bg-card pb-3">
            <span className="font-serif text-sm font-bold text-foreground">{t("appName")}</span>
            <button
              type="button"
              className="rounded-full bg-muted p-2 text-muted-foreground hover:bg-secondary hover:text-foreground"
              onClick={() => setMobileNav(false)}
              aria-label="Close menu"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          <div className="flex flex-col gap-2">
            {navItems.map((item) => (
              <a
                key={item.href}
                href={item.href}
                className="rounded-xl border border-border bg-muted px-4 py-3 text-sm font-medium text-foreground transition-colors hover:bg-secondary"
                style={{ backgroundColor: "hsl(var(--muted))" }}
                onClick={() => setMobileNav(false)}
              >
                {item.label}
              </a>
            ))}
          </div>
          <Link
            to={adminTo}
            className="mt-4 rounded-xl border border-border bg-muted py-2.5 text-center text-sm font-semibold text-foreground transition-colors hover:bg-secondary"
            style={{ backgroundColor: "hsl(var(--muted))" }}
            onClick={() => setMobileNav(false)}
          >
            {t("admin")}
          </Link>
        </nav>
      </div>,
      document.body,
    );

  return (
    <header className="sticky top-0 z-40 border-b border-border/60 bg-background/90 backdrop-blur-md">
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between gap-3 px-4 sm:px-6">
        <Link to="/" className="flex min-w-0 items-center gap-2">
          <span className="icon-tile h-9 w-9 shrink-0">
            <Sparkles className="h-4 w-4" />
          </span>
          <span className="truncate font-serif text-base font-bold text-foreground sm:text-lg">
            {t("appName")}
          </span>
        </Link>
        <nav className="hidden items-center gap-8 text-sm font-medium text-muted-foreground md:flex">
          {navItems.map((item) => (
            <a key={item.href} href={item.href} className="transition-colors hover:text-foreground">
              {item.label}
            </a>
          ))}
        </nav>
        <div className="flex shrink-0 items-center gap-1.5 sm:gap-2">
          <LanguageSwitcher compact />
          <Link
            to={adminTo}
            className="hidden rounded-full border border-border bg-card px-4 py-2 text-sm font-semibold text-muted-foreground transition-colors hover:bg-muted hover:text-foreground sm:inline-flex"
          >
            {t("admin")}
          </Link>
          <Link
            to="/login"
            className="btn-primary max-w-[11rem] px-3 py-2 text-xs sm:max-w-none sm:px-4 sm:text-sm"
          >
            <Download className="h-4 w-4 shrink-0" />
            <span className="truncate">{t("openApp")}</span>
          </Link>
          <button
            type="button"
            aria-expanded={mobileNav}
            aria-label="Open menu"
            className="inline-flex rounded-full border border-border bg-card p-2 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground md:hidden"
            onClick={() => setMobileNav(true)}
          >
            <Menu className="h-5 w-5" />
          </button>
        </div>
      </div>

      {mobileMenu}
    </header>
  );
}
